// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const CYBERSECURITY_ASSURANCE_INDEX_WORDS = {
    // ── Generic values ──
    none: { en: '—', vi: '—' },
    yes: { en: 'Yes', vi: 'Có' },
    no: { en: 'No', vi: 'Không' },
    notRecorded: { en: 'Not recorded', vi: 'Chưa ghi nhận' },
    labelNotReadable: { en: 'Label could not be read', vi: 'Không đọc được nhãn' },
    // ── The export label (AC15): the first line of every export's first page ──
    selfAssessmentLabel: {
        en: 'The organisation’s own self-assessment against the pilot scale; not an Index result',
        vi: 'Kết quả tự đánh giá của chính tổ chức theo thang tự đánh giá thí điểm; không phải kết quả Chỉ số',
    },
    // ── Recording states (OQ-3) ──
    stateRecorded: { en: 'Recorded', vi: 'Đã ghi nhận' },
    stateAwaiting: { en: 'Awaiting a Mức', vi: 'Chờ ghi nhận Mức' },
    stateExcludedWithEvidence: { en: 'Excluded with reason', vi: 'Loại trừ có lý do' },
    stateExclusionNotEvidenced: {
        en: 'Exclusion not evidenced — Mức 0 for the period',
        vi: 'Loại trừ chưa có tài liệu chứng minh — Mức 0 trong kỳ đánh giá',
    },
    atFloor: { en: 'Mức 0', vi: 'Mức 0' },
    // ── Offer and level (AC4, CL-2) ──
    offerNotOfferedBelowLevel: {
        en: 'Not offered at this system’s level',
        vi: 'Không áp dụng ở cấp độ của hệ thống này',
    },
    levelNotApproved: {
        en: 'Level not approved: {group} cannot be determined',
        vi: 'Cấp độ chưa được phê duyệt: chưa xác định được {group}',
    },
    offerSoftwareDevelopmentNotRecorded: {
        en: 'Not offered: no software development is recorded for this system',
        vi: 'Không áp dụng: hệ thống chưa ghi nhận hoạt động phát triển phần mềm',
    },
    offerQualifierUnreadable: {
        en: 'Not offered: the installed catalogue’s level qualifier for this item could not be read',
        vi: 'Không áp dụng: không đọc được điều kiện cấp độ của mục này trong danh mục đã cài đặt',
    },
    classificationChanged: {
        en: 'Classification changed since opening',
        vi: 'Phân loại đã thay đổi kể từ khi mở kỳ',
    },
    levelFromClassification: {
        en: 'Read from the approved classification',
        vi: 'Đọc từ phân loại đã được phê duyệt',
    },
    levelEntered: {
        en: 'Entered with approval reference {ref}',
        vi: 'Nhập kèm văn bản phê duyệt {ref}',
    },
    // ── Confidentiality (Slice D, D2) ──
    restricted: { en: 'Restricted', vi: 'Hạn chế truy cập' },
    restrictedEvidenceNoFile: {
        en: 'Restricted evidence: no file is held here',
        // Remediation W (legal-s45 row 34): "hạn chế" alone reads as limited or
        // insufficient evidence; the evidence is *of the restricted kind*.
        vi: 'Tài liệu chứng minh thuộc diện hạn chế: không lưu tệp tại đây',
    },
    pilotBodyNotOffered: {
        en: 'Not offered on this deployment',
        vi: 'Không cung cấp trên hệ thống triển khai này',
    },
    // ── Supplier requests (Slice E) ──
    requestOutstanding: { en: 'Outstanding', vi: 'Chưa phản hồi' },
    requestReplied: { en: 'Replied on {date}', vi: 'Đã phản hồi ngày {date}' },
    providedBySupplier: { en: 'Provided by the supplier', vi: 'Do nhà cung cấp cung cấp' },
    linkWithheld: {
        en: 'Linked record (name not shown: you do not hold the read permission of its register)',
        vi: 'Hồ sơ được liên kết (không hiển thị tên: bạn không có quyền xem sổ chứa hồ sơ này)',
    },
    // ── Statistics (Slice C, D9) ──
    derivedOnRead: { en: 'Derived on read: {formula}', vi: 'Tính khi hiển thị: {formula}' },
    // Remediation W (U1) — why no value is derived (`cybersecurityAssuranceIndexDerivedText`).
    derivedNoDenominator: {
        en: 'Not derived (no denominator)',
        vi: 'Không tính (không có mẫu số)',
    },
    derivedDenominatorZero: {
        en: 'Not derived (denominator is 0)',
        vi: 'Không tính (mẫu số bằng 0)',
    },
    derivedFormulaUnreadable: {
        en: 'Not derived: the factor in the installed formula could not be read',
        vi: 'Không tính: không đọc được hệ số trong công thức đã cài đặt',
    },
    summedCounts: {
        en: 'Summed counts of the systems recorded',
        vi: 'Tổng các số liệu của các hệ thống đã ghi nhận',
    },
    // ── Export frame ──
    exportPeriod: { en: 'Assessment period {start} – {end}', vi: 'Kỳ đánh giá {start} – {end}' },
    exportOrganization: { en: 'Organization: {name}', vi: 'Tổ chức: {name}' },
    exportGeneratedAt: { en: 'Generated: {instant}', vi: 'Thời điểm tạo: {instant}' },
    exportGeneratedBy: { en: 'Generated by: {name}', vi: 'Người tạo: {name}' },
    exportPackVersion: {
        en: 'Assessed against pack version {version}, catalogue {catalogue}',
        vi: 'Đánh giá theo phiên bản gói {version}, danh mục {catalogue}',
    },
    exportSystem: { en: 'System: {name} — {level}', vi: 'Hệ thống: {name} — {level}' },
    colItem: { en: 'Item', vi: 'Nội dung' },
    colTitle: { en: 'Title', vi: 'Tên' },
    colState: { en: 'Recording state', vi: 'Trạng thái ghi nhận' },
    colRating: { en: 'Mức', vi: 'Mức' },
    colExplanation: { en: 'Explanation', vi: 'Thuyết minh' },
    colEvidence: { en: 'Evidence', vi: 'Tài liệu chứng minh' },
    colStatistic: { en: 'Statistic', vi: 'Số liệu' },
    colNumerator: { en: 'Numerator', vi: 'Tử số' },
    colDenominator: { en: 'Denominator', vi: 'Mẫu số' },
    colUnit: { en: 'Unit', vi: 'Đơn vị' },
    colSource: { en: 'Evidence source', vi: 'Nguồn số liệu' },
    colCalculationVersion: { en: 'Calculation version', vi: 'Phiên bản cách tính' },
    colAuthorityFigure: {
        en: 'Competent authority’s figure',
        vi: 'Số liệu cơ quan chức năng ghi nhận',
    },
    colScope: { en: 'Scope', vi: 'Phạm vi' },
    scopePeriod: { en: 'Whole period', vi: 'Toàn kỳ' },
    headingItems: { en: 'Items', vi: 'Các nội dung' },
    headingStatistics: { en: 'Statistics', vi: 'Số liệu thống kê' },
    headingSupplierRequests: {
        en: 'Supplier Evidence Requests',
        vi: 'Yêu cầu nhà cung cấp cung cấp tài liệu chứng minh',
    },
    colVendor: { en: 'Supplier', vi: 'Nhà cung cấp' },
    colRequestedOn: { en: 'Requested on', vi: 'Ngày yêu cầu' },
    colRequestState: { en: 'State', vi: 'Trạng thái' },
    // ── Remediation W (U3, U4) — the export carries what the pages carry ──
    // E12.5 p1 (BE-14): no word here names a group. A group prints as the pack's
    // `group_label` (or as authored); the instrument's own word for a group is
    // the pack's, never a literal of this table (R-46).
    headingPeriod: { en: 'The period', vi: 'Thông tin kỳ đánh giá' },
    colField: { en: 'Recorded', vi: 'Thông tin' },
    colValue: { en: 'Value', vi: 'Nội dung ghi nhận' },
    fieldTitle: { en: 'Title', vi: 'Tên kỳ đánh giá' },
    fieldAssessorRole: { en: 'Assessing role', vi: 'Vai trò đánh giá' },
    fieldGuidanceReference: {
        en: 'Standing-body guidance applied',
        vi: 'Hướng dẫn của cơ quan thường trực được áp dụng',
    },
    fieldStateSecrets: { en: 'State secrets', vi: 'Bí mật nhà nước' },
    fieldStatus: { en: 'Status', vi: 'Trạng thái' },
    statusOpen: { en: 'Open', vi: 'Đang mở' },
    statusClosed: { en: 'Closed on {date} by {name}', vi: 'Đã đóng ngày {date}, người đóng: {name}' },
    fieldMembers: {
        en: 'People who may read this period',
        vi: 'Những người được xem kỳ đánh giá này',
    },
    fieldNotes: { en: 'Notes', vi: 'Ghi chú' },
    fieldOwnerName: { en: 'Owner of the information system', vi: 'Chủ quản hệ thống thông tin' },
    fieldOwnerSystemName: { en: 'Owner’s information system', vi: 'Hệ thống thông tin của chủ quản' },
    fieldOwnerParticipantKind: { en: 'Taking part as', vi: 'Phối hợp với tư cách' },
    fieldOwnerRequestReference: {
        en: 'The owner’s request (reference)',
        vi: 'Văn bản đề nghị của chủ quản (số hiệu)',
    },
    fieldOwnerRequestDate: { en: 'Date of the owner’s request', vi: 'Ngày đề nghị của chủ quản' },
    fieldParticipationScope: { en: 'Scope taken part in', vi: 'Phạm vi phối hợp' },
    fieldOwnerProcedures: {
        en: 'The owner’s rules and procedures followed',
        vi: 'Quy định, quy trình của chủ quản được tuân thủ',
    },
    fieldOwnerVendor: { en: 'Owner in the vendor register', vi: 'Chủ quản trong sổ nhà cung cấp' },
    headingOwnerAssessment: {
        en: 'The owner’s assessment taken part in',
        vi: 'Việc đánh giá của chủ quản mà tổ chức tham gia',
    },
    headingSystems: { en: 'Systems in scope', vi: 'Hệ thống trong phạm vi' },
    colSystem: { en: 'System', vi: 'Hệ thống' },
    colLevel: { en: 'Level', vi: 'Cấp độ' },
    colLevelSource: { en: 'Where the level comes from', vi: 'Nguồn của cấp độ' },
    colClassificationChanged: {
        en: 'Classification since opening',
        vi: 'Phân loại kể từ khi mở kỳ',
    },
    classificationUnchanged: { en: 'Unchanged', vi: 'Không thay đổi' },
    colSoftwareDevelopment: { en: 'Software development', vi: 'Phát triển phần mềm' },
    colOperatingParties: {
        en: 'Who administers, operates or secures it',
        vi: 'Đơn vị quản trị, vận hành, bảo đảm an ninh mạng',
    },
    colExclusion: { en: 'Exclusion: scope and reason', vi: 'Loại trừ: phạm vi và lý do' },
    colParticipants: { en: 'Parties taking part', vi: 'Các bên phối hợp' },
    colEnteredBy: { en: 'Entered by', vi: 'Người nhập' },
    participantVendor: { en: 'vendor register: {name}', vi: 'sổ nhà cung cấp: {name}' },
    certificationBasis: {
        en: 'Certification basis: {standard}; certificate {reference}; the organisation’s judgement of what it covers: {judgement}; judged by {name}',
        vi: 'Căn cứ chứng nhận: {standard}; số chứng nhận {reference}; nhận định của tổ chức về phạm vi chứng nhận: {judgement}; người nhận định: {name}',
    },
    dateStatementMade: { en: 'Statement made: {statement}', vi: 'Đã xác nhận: {statement}' },
    evidenceCitedFromRequest: {
        en: 'Cited from the request to {supplier} of {date}',
        vi: 'Trích từ yêu cầu gửi {supplier} ngày {date}',
    },
    evidenceCitedNotHeld: {
        en: 'Cited from a supplier request whose evidence is no longer held',
        vi: 'Trích từ yêu cầu nhà cung cấp mà tài liệu chứng minh không còn được lưu giữ',
    },
    colDerivedValue: { en: 'Value', vi: 'Giá trị' },
    colNotes: { en: 'Notes', vi: 'Ghi chú' },
    colRowCount: { en: 'Systems recorded', vi: 'Số hệ thống đã ghi nhận' },
    colNumeratorSum: { en: 'Numerator, summed', vi: 'Tổng tử số' },
    colDenominatorSum: { en: 'Denominator, summed', vi: 'Tổng mẫu số' },
    colEvidenceKind: { en: 'Evidence requested', vi: 'Tài liệu chứng minh được yêu cầu' },
    colRequestedBy: { en: 'Requested by', vi: 'Người yêu cầu' },
    colRequestReference: { en: 'Request reference', vi: 'Số hiệu văn bản yêu cầu' },
    colContractReference: { en: 'Contract', vi: 'Hợp đồng' },
    colAnswerWantedBy: {
        en: 'Answer wanted by (your organization’s own date)',
        vi: 'Mong muốn nhận phản hồi trước ngày (ngày của chính tổ chức bạn)',
    },
    colResponseSummary: { en: 'What the supplier provided', vi: 'Nội dung nhà cung cấp cung cấp' },
    colCertificationReference: { en: 'Certification reference', vi: 'Số chứng nhận' },
    colEvidenceReturned: { en: 'Evidence returned', vi: 'Tài liệu chứng minh nhận được' },
};
/** The word for `key` in `locale` (VI for `vi` / `vi-*`, else EN), `{name}` filled from `values`. */
export function cybersecurityAssuranceIndexWord(key, locale, values = {}) {
    const pair = CYBERSECURITY_ASSURANCE_INDEX_WORDS[key];
    const text = locale === 'vi' || locale.startsWith('vi-') ? pair.vi : pair.en;
    return text.replace(/\{(\w+)\}/g, (whole, name) => values[name] ?? whole);
}
//# sourceMappingURL=cybersecurity-assurance-index-words.js.map